/**
 * The transient "a new one just arrived" flash.
 *
 * A message/comment created seconds ago by someone other than the viewer flashes its
 * background once, then settles. Historical messages loaded on thread open (created before
 * the window) and the viewer's own messages never flash — the flash is arrival feedback,
 * not an unread marker (that is the persistent dot / separator).
 *
 * The whole decision — recency AND own-ness — is frozen at mount with a lazy `useState`
 * initializer and never revoked: the `animate-highlight` CSS animation owns the fade-out
 * (see `global.css`), so this hook only decides whether to START it. The returned value is
 * therefore stable for the component's lifetime. Nothing is re-checked live: a flag that
 * flipped back to `false` on a later render (a timer, or own-ness resolving a tick after
 * mount) would strip the class mid-animation and snap the background away.
 */

import { differenceInSeconds, isValid, parseISO } from "date-fns";
import { useState } from "react";

/** How recent a message must be, at mount, to flash as "just arrived". */
const JUST_ARRIVED_WINDOW_S = 5;

export function useJustArrivedHighlight(
  createdAt: string | number | Date | null | undefined,
  isOwnMessage: boolean,
): boolean {
  const [justArrivedAtMount] = useState(() => {
    if (isOwnMessage) {
      return false;
    }
    if (!createdAt) {
      return false;
    }
    const created =
      typeof createdAt === "string" ? parseISO(createdAt) : new Date(createdAt);
    return (
      isValid(created) &&
      differenceInSeconds(new Date(), created) < JUST_ARRIVED_WINDOW_S
    );
  });

  return justArrivedAtMount;
}
